import React, { useMemo } from "react";

/**
 * FragmentReveal — premium "digital emergence" intro that builds the hero
 * outward from the bottom-center.
 *
 * Renders a fine grid of small white covering rectangles (cols × rows).
 * Each cell fades out (opacity 1→0, micro scale-down) with a delay tied to
 * its weighted distance from the bottom-center origin, plus deterministic
 * jitter for organic feel.
 *
 * `startOffset` shifts every cell's delay backwards (negative), so the
 * origin cells are already past their fade-out at first paint — that means
 * the user NEVER sees a fully-white hero, only a small revealed cluster at
 * the bottom-center that grows outward.
 *
 * Pure CSS animations driven by inline `animation-delay` — fires reliably
 * on every mount/refresh. Hidden entirely under `prefers-reduced-motion`.
 */
export default function FragmentReveal({
  cols = 44,
  rows = 18,
  duration = 0.4,
  spreadDelay = 0.7,
  jitter = 0.12,
  startOffset = -0.25,
  className = "",
}) {
  const cells = useMemo(() => {
    const ox = (cols - 1) / 2;
    const oy = rows - 0.5; // just below bottom row

    const maxDx = Math.max(ox, cols - 1 - ox);
    const maxDy = oy;

    const list = [];
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const dx = (c - ox) / maxDx;
        const dy = (oy - r) / maxDy;
        // Pythagorean distance, slight vertical bias so upward growth feels longer.
        const dist = Math.sqrt(dx * dx + dy * dy * 1.05) / 1.45;
        const normalized = Math.min(1, dist);
        const seed = Math.sin((r * 73.13 + c * 19.91) * 12.9898) * 43758.5453;
        const rand = (seed - Math.floor(seed)) * jitter;
        list.push({
          r,
          c,
          delay: normalized * spreadDelay + rand + startOffset,
        });
      }
    }
    return list;
  }, [cols, rows, spreadDelay, jitter, startOffset]);

  const cellW = 100 / cols;
  const cellH = 100 / rows;

  return (
    <div
      aria-hidden
      className={`aw-fragment-reveal pointer-events-none absolute inset-0 z-30 overflow-hidden ${className}`}
      data-testid="fragment-reveal">
      {cells.map(({ r, c, delay }, i) => (
        <div
          key={i}
          className="aw-fragment"
          style={{
            top: `${r * cellH}%`,
            left: `${c * cellW}%`,
            width: `calc(${cellW}% + 0.5px)`,
            height: `calc(${cellH}% + 0.5px)`,
            animationDelay: `${delay}s`,
            animationDuration: `${duration}s`,
          }}
        />
      ))}
    </div>
  );
}
